<template>
  <div>
    <v-dialog v-model="dialog" width="500" scrollable>
      <v-card id="areaEdit">
        <v-card-title v-if="modalMode === 'create'" class="secondary">{{$t('add-area')}}</v-card-title>
        <v-card-title v-if="modalMode === 'edit'" class="secondary">{{$t('edit-area')}}</v-card-title>
        <v-card-text>
          <v-form @submit.stop.prevent="handleSubmit" id="areaEditForm">
            <v-row justify="center">
              <v-col cols="11">
                <!-- name -->
                <v-text-field
                  :label="$t('name')"
                  tabindex="1"
                  :dense="true"
                  v-model="form.name"
                  outlined
                  :error-messages="nameErrors"
                  @input="$v.form.name.$touch()"
                  @blur="$v.form.name.$touch()"
                  color="secondary"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row justify="center">
              <v-col cols="11">
                <!-- site -->
                <v-text-field
                  :label="$t('site')"
                  :dense="true"
                  disabled
                  v-model="form.site.value"
                  outlined
                  color="secondary"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="negative-mt" cols="11">
                <v-radio-group :disabled="modalMode === 'edit'" v-model="form.outdoorIndoor">
                  <v-row class="ml-1">
                    <v-col>
                      <v-radio :label="$t('indoor')" :value="'indoor'"></v-radio>
                    </v-col>
                    <v-col>
                      <v-radio :label="$t('outdoor')" :value="'outdoor'"></v-radio>
                    </v-col>
                  </v-row>
                </v-radio-group>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-col class="ma-auto" cols="11">
            <v-btn
              id="areaSubmitBtn"
              depressed
              color="secondary"
              tabindex="3"
              @click="handleSubmit"
              class="px-8 float-right"
            ><span v-if="modalMode === 'edit'">{{$t('save')}}</span><span v-if="modalMode === 'create'">{{$t('add')}}</span></v-btn>
            <v-btn
              v-if="modalMode === 'edit' && form.outdoorIndoor === 'outdoor'"
              depressed
              color="secondary"
              tabindex="3"
              @click="handleEditOutdoorArea"
              class="float-left"
            >{{$t('edit-area-polygon')}}</v-btn>
            <v-btn
              text
              color="secondary"
              tabindex="3"
              @click="resetForm"
              class="px-8 float-right"
            >{{$t('cancel')}}</v-btn>
          </v-col>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <indoor-area-image-upload @areaCreatedEdited="areaCreated" :area="form" :modalMode="modalMode" ref="imageUpload" />
    <outdoor-area-polygon-upload @areaCreatedEdited="areaCreated" :area="form" :modalMode="modalMode" ref="mapPolygonUpload" />
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import UploadImageArea from './indoor-area-image-upload';
import UploadMapPolygonArea from './outdoor-area-polygon-upload';

export default {
  components: {
    'indoor-area-image-upload': UploadImageArea,
    'outdoor-area-polygon-upload': UploadMapPolygonArea,
  },
  data() {
    return {
      dialog: false,
      form: {
        name: '',
        outdoorIndoor: 'indoor',
        id: '',
        polygon: null,
        site: {},
        zones: []
      },
    };
  },
  mixins: [validationMixin],
  validations: {
    form: {
      name: {
        required,
      },
      id: '',
    },
  },
  props: ['modalMode'],
  computed: {
    nameErrors() {
      const errors = [];
      if (!this.$v.form.name.$dirty) return errors;
      !this.$v.form.name.required &&
        errors.push(this.$t('error-name-required'));
      return errors;
    },
  },
  methods: {
    ...mapActions('area', ['editArea']),
    openModal(area, site) {
      this.dialog = true;
      this.$v.form.$reset();
      if (site) {
        this.$set(this.form, 'site', site);
      }
      if (area) {
        Object.assign(this.form, {
          name: area.name,
          id: area.id,
          polygon: area.polygon,
          outdoorIndoor: area.areaLocation,
          zones: area.zones
        });
      } else {
        Object.assign(this.form, {
          name: '',
        });
      }
    },
    handleCreateArea() {
      this.$v.$touch();
      if (this.$v.form.$anyError) {
        return;
      }
      if (this.form.outdoorIndoor === 'indoor') {
        this.dialog = false;
        this.$refs.imageUpload.openModal();
      } else if(this.form.outdoorIndoor === 'outdoor') {
        this.dialog = false;         
        this.$refs.mapPolygonUpload.openModal();
      }
    },
    areaCreated(area) {
      this.$emit('areaCreatedEdited', area)
      this.dialog = false;
    },
    handleSubmit() {
      if (this.modalMode === 'create') {
        this.handleCreateArea();
      } else if (this.modalMode === 'edit') {
        this.hadleEditArea();
      }
    },
    handleEditOutdoorArea() {
      this.$v.$touch();
      if (this.$v.form.$anyError) {
        return;
      }
      this.$refs.mapPolygonUpload.openModal();
      this.dialog = false;
    },
    async hadleEditArea() {
      this.$v.$touch();
      if (this.$v.form.$anyError) {
        return;
      }
      try {
        let payload = {
          data: {
            name: this.form.name,
          },
          id: this.form.id,
        };
        const editedArea = await this.editArea(payload);
        this.$toasted.show(this.$t('area-edited'), {
          position: 'bottom-right',
          className: ['toast-success'],
          duration: 2000,
        });
        this.dialog = false;
        this.$emit('areaCreatedEdited', editedArea)
      } catch (error) {
        this.$toasted.show(error, {
          position: 'bottom-right',
          className: ['toast-error'],
          duration: 5000,
        });
        throw new Error('Something went wrong', e);
      }
    },
    resetForm() {
      this.$v.form.$reset();
      Object.assign(this.form, {
        name: '',
        id: ''
      });
      this.dialog = false;
    },
  },
};
</script>

<style scoped>
#areaEditForm {
  padding-top: 2rem;
}
.negative-mt {
  margin-top: -20px;
  margin-bottom: -19px;
}
</style>